'use strict';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import {getOrganization} from '../../app/model/reducers/state';
import {AwsS3RequestService} from '../aws-api/AwsS3RequestService';
import {getId} from '../domain/ReduxUtils';


const angular = require('angular');
const {copy, extend, forEach} = angular;

module.exports = FileStackProvider;

// TODO: if possible cache the last service the user selected from and default to it each time, assuming filepicker doesn't already do this for you
function FileStackProvider() {
  var __apiKey;
  var __storeContainer;

  this.apiKey = function (v) {
    if (typeof v === 'undefined') {
      return apiKey;
    }
    __apiKey = v;
  };

  this.storeContainer = function (v) {
    if (typeof v === 'undefined') {
      return __storeContainer;
    }
    __storeContainer = v;
  };


  this.$get = [
    '$window',
    '$log',
    '$timeout',
    '$http',
    '$injector',
    '$parse',
    '$ngRedux',
    AwsS3RequestService.SERVICE_NAME,
    'FileUtilities',
    'FileStackNotificationsService',
    /**
     * @param $window
     * @param $log
     * @param $timeout
     * @param $http
     * @param $injector
     * @param $parse
     * @param $ngRedux
     * @param {AwsS3RequestService} awsS3RequestService
     * @param FileUtilities
     * @param FileStackNotificationsService
     * @return {{pickFile: (function(*=): *), pickFiles: pickFiles}}
     */
    function (
      $window,
      $log,
      $timeout,
      $http,
      $injector,
      $parse,
      $ngRedux,
      awsS3RequestService,
      FileUtilities,
      FileStackNotificationsService
    ) {
      __verifyConfig();

      $window.filepicker.setKey(__apiKey);

      // 'COMPUTER' MUST be first
      var services = [
        'COMPUTER',
        'ALFRESCO',
        'BOX',
        'CLOUDDRIVE',
        'DROPBOX',
        'FACEBOOK',
        'GITHUB',
        'GOOGLE_DRIVE',
        'FLICKR',
        'EVERNOTE',
        'GMAIL',
        'INSTAGRAM',
        'SKYDRIVE',
        'IMAGE_SEARCH',
        'URL',
        'WEBCAM',
        'VIDEO',
        'PICASA',
        'FTP',
        'WEBDAV',
        'CLOUDAPP',
        'IMGUR'
      ];

      //if (!DeviceServices.isWebView()) services.shift(); // Inject DeviceServices if this is re-enabled

      // TODO(i18n): set the user's current language here {language: }
      // TODO: implement security policy using the Gateway Server to generate an appropriate policy for the user and their currently selected org
      //       {policy: POLICY, signature: SIGNATURE}
      var defaultPickerOptions = {
        mimetype: '*/*',
        multiple: true,
        folders: false,
        container: 'modal',
        services: services,
        backgroundUpload: true,
        hide: true
      };

      var defaultStoreOptions = {
        location: 'S3',
        container: __storeContainer
      };

      return {
        pickFile: pickFile,
        pickFiles: pickFiles
      };

      function pickFile(uploadRequest) {
        return pickFiles(uploadRequest, {multiple: false})
      }

      /**
       * Docs: https://www.filestack.com/docs/file-ingestion/javascript-api/pick-and-store
       * @param {Object} uploadRequest
       * @param {string} [uploadRequest.path='/'] - the path to store at or the initial 'pick' path if getPath is provided
       * @param {string} [uploadRequest.firebasePath]
       * @param {Function} [uploadRequest.getPath=null] - must return an object with {path, firebasePath}
       * @param {string} uploadRequest.usage
       * @param {Object} [uploadRequest.attachable=null]
       * @param {string} uploadRequest.onSuccess
       * @param {Object} uploadRequest.onSuccessLocals
       * @param pickerOptions
       * @param storeOptions
       * @returns {*}
       */
      function pickFiles(uploadRequest, pickerOptions, storeOptions) {
        const rootState = $ngRedux.getState();
        const organization = getOrganization(rootState);
        if (!organization) return Promise.reject('No organization selected');

        var deferred = Promise.defer();
        var filePromises = [];

        // Closing over uploadRequest
        uploadRequest.path = uploadRequest.path || '/';
        pickerOptions = extend({}, defaultPickerOptions, pickerOptions || {});
        storeOptions = extend({}, defaultStoreOptions, storeOptions || {});

        storeOptions.path = uploadRequest.path;

        // Closing over currently selected org ID
        const orgId = getId(organization);
        awsS3RequestService.requestFilestackPick({
          organizationId: orgId,
        }).then(function withPickAuthorization(response) {
          pickerOptions.policy = response.policy;
          pickerOptions.signature = response.signature;
          //console.log('pick policy', response.policy);
          //console.log('pick signature', response.signature);

          $window.filepicker.pickMultiple(pickerOptions, onPickSuccess, onError, onPickProgress);
        }, deferred.reject.bind(deferred));

        //transform progress for first round progress
        function onPickProgress(fpProgress) {
          var note = FileStackNotificationsService.createNotification(fpProgress, uploadRequest);
          note.progressSubject.next(fpProgress.progress / 2);
        }

        function onPickSuccess(blobs) {
          $log.debug('picked blobs', blobs);
          $log.debug('stack cache', FileStackNotificationsService.items);

          // TODO: tell the server to delete all of the these
          var cancelledBlobs = FileStackNotificationsService.getCancelledBlobs(blobs);

          //       include all of the information that would be sent in a direct upload request

          // for each Blob: make a filestack/store request to server and receive authorized s3Key and upload policy
          // This triggers creation of a new FileDomain instance in pending state
          // On receipt, store object with the new path, name, and policy params
          forEach(blobs, function eachBlob(blob) {
            var note = FileStackNotificationsService.createNotification(blob, uploadRequest);
            var filePromise = __generateGetStoreAuthorization(blob, uploadRequest, orgId /* Closed over above */)
              .then(__generateWithStoreAuthorization(blob, storeOptions, onPickProgress))
              .then(__generateAfterStoreSuccess(blob))
              .then(__generateVerifyUploadSuccess())
              .then(__generateOnStoreConversionComplete(uploadRequest.onSuccess, uploadRequest.onSuccessLocals))
              .then(note.deferred.resolve.bind(note.deferred), note.deferred.reject.bind(note.deferred));

            //transform progress for first round progress
            function onPickProgress(progress) {
              note.progress = 50 + progress / 2;
              note.progressSubject.next(50 + progress / 2);
            }

            filePromises.push(filePromise);
          });

          Promise.all(filePromises).then(deferred.resolve.bind(deferred), deferred.reject.bind(deferred));
        }

        function onError(error) {
          var cancelledBlobs = FileStackNotificationsService.getCancelledBlobs({});
          if (error.code === 101) {
            deferred.resolve(null);
          } else {
            deferred.reject(error);
          }
        }

        return deferred.promise;
      }

      // TODO: convert avatars to use FileStack and their CDN

      function __generateGetStoreAuthorization(blob, uploadRequest, orgId) {
        var filename = uploadRequest.filename;
        var path = uploadRequest.path;
        var firebasePath = uploadRequest.firebasePath;
        var varPaths;

        if (isFunction(uploadRequest.getFilename)) {
          filename = uploadRequest.getFilename();
        }

        filename = filename + '.' + FileUtilities.getExtension(blob.filename);

        if (isFunction(uploadRequest.getPath)) {
          varPaths = uploadRequest.getPath();
          path = varPaths.path;
          firebasePath = varPaths.firebasePath;
        }

        return awsS3RequestService.requestFilestackStore({
          org: orgId,

          handle: __getHandle(blob.url), // TODO: might need to pass just the id here?
          filename: filename,
          mimetype: blob.mimetype,

          path: path,
          firebasePath: firebasePath,
          usage: uploadRequest.usage,
          attachable: uploadRequest.attachable || null
        })
      }

      function __generateWithStoreAuthorization(blob, storeOptions, onStoreProgress) {
        return function withStoreAuthorization(response) {
          var fileDeferred = Promise.defer();
          var fileStoreOptions = copy(storeOptions);
          //fileStoreOptions.base64decode = false;
          fileStoreOptions.mimetype = blob.mimetype;
          fileStoreOptions.filename = blob.filename;
          fileStoreOptions.path = response.path + '/' + response.name;
          fileStoreOptions.policy = response.policy;
          fileStoreOptions.signature = response.signature;

          //console.log('store policy', response.policy);
          //console.log('store signature', response.signature);

          //console.log('store blob', blob);
          //console.log('store options', fileStoreOptions);

          $window.filepicker.storeUrl(blob.url + '?policy=' + response.policy + '&signature=' + response.signature,
            fileStoreOptions, onStoreSuccess, onStoreError, onStoreProgress);

          return fileDeferred.promise;

          function onStoreSuccess(blob) {
            fileDeferred.resolve({
              fileId: response.firebaseId,
              blob: blob
            });
          }

          // send a command to cancel the upload to the gateway server
          function onStoreError(error) {
            console.error(error);

            fileDeferred.reject({
              fileId: response.firebaseId,
              blob: blob
            });
          }
        }
      }

      function __generateAfterStoreSuccess(blob) {
        return function afterStoreSuccess({fileId}) {
          return awsS3RequestService
            .reportFilestackStoreSuccess({
              fileId,
            })
            // pass just the file id to the next step in the chain
            .then(() => fileId);
        }
      }

      function __generateVerifyUploadSuccess() {
        return function verifyUploadSuccess(fileId) {
          return awsS3RequestService
            .reportUploadSuccess({
              fileId,
            })
          // pass just the file id to the next step in the chain
            .then(() => fileId);
        }
      }

      function __generateOnStoreConversionComplete(onSuccess, onSuccessLocals) {
        return function onStoreConversionComplete(fileId) {
          var handler = $parse(onSuccess);
          var locals = extend({},
            isObject(onSuccessLocals) ? onSuccessLocals : null,
            {fileDomain: fileId}
          );

          try {
            var result = handler($injector, locals);

            if (result && isFunction(result.then)) {
              return result.then(function () {
                return fileId; // pass just the file id to the next step in the chain
              })
            } else {
              return fileId;
            }
          } catch (e) {
            return fileId;
          }
        }
      }

      function __getHandle(blobUrl) {
        if (!blobUrl) return '';
        var split = blobUrl.split('/');
        if (!split.length) return '';
        return split[split.length - 1];
      }

      function __verifyConfig() {
        var hasConfig = {
          apiKey: __apiKey,
          defaultStore: __storeContainer
        };

        if (process.env.ENVIRONMENT === 'development') {
          forEach(hasConfig, function (value, configKey) {
            if (!value) {
              console.warn('FileStackProvider:Missing configuration for ' + configKey);
            }
          });
        }
      }
    }]
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/upload-service/FileStack.js